import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCases } from "@/hooks/useCases";
import { useCaseStore } from "@/stores/caseStore";
import { Briefcase, ChevronDown, X } from "lucide-react";

export function CasePicker() {
  const navigate = useNavigate();
  const activeCaseId = useCaseStore((s) => s.activeCaseId);
  const activeCaseName = useCaseStore((s) => s.activeCaseName);
  const setActiveCase = useCaseStore((s) => s.setActiveCase);
  const clearActiveCase = useCaseStore((s) => s.clearActiveCase);

  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  const { data } = useCases({ status: "open", q: q || undefined, pageSize: 10 });

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm transition-colors ${
          activeCaseId
            ? "border-accent-300 bg-accent-50 text-accent-700 dark:border-accent-700 dark:bg-accent-950 dark:text-accent-400"
            : "border-border bg-surface text-text-secondary hover:bg-surface-raised"
        }`}
      >
        <Briefcase className="h-3.5 w-3.5" />
        <span className="max-w-[160px] truncate">
          {activeCaseId ? activeCaseName : "All Entities"}
        </span>
        {activeCaseId ? (
          <X
            className="h-3.5 w-3.5 hover:text-accent-900 dark:hover:text-accent-200"
            onClick={(e) => {
              e.stopPropagation();
              clearActiveCase();
            }}
          />
        ) : (
          <ChevronDown className="h-3.5 w-3.5" />
        )}
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-72 rounded-lg border border-border bg-surface-overlay shadow-lg">
          <div className="border-b border-border p-2">
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search cases..."
              className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-focus-ring"
            />
          </div>

          <div className="max-h-60 overflow-y-auto p-1">
            {activeCaseId && (
              <button
                type="button"
                className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-text-secondary hover:bg-neutral-100 dark:hover:bg-neutral-800"
                onClick={() => {
                  clearActiveCase();
                  setOpen(false);
                }}
              >
                <X className="h-3.5 w-3.5" />
                Clear — show all entities
              </button>
            )}

            {data?.items.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors ${
                  activeCaseId === c.id
                    ? "bg-accent-100 text-accent-700 dark:bg-neutral-800 dark:text-accent-400"
                    : "text-text-primary hover:bg-neutral-100 dark:hover:bg-neutral-800"
                }`}
                onClick={() => {
                  setActiveCase(c.id, c.name);
                  setOpen(false);
                  setQ("");
                }}
              >
                <Briefcase className="h-3.5 w-3.5 shrink-0" />
                <span className="flex-1 truncate text-left">{c.name}</span>
                <span className="text-xs text-text-muted">{c.reference}</span>
              </button>
            ))}

            {data?.items.length === 0 && (
              <p className="px-3 py-2 text-sm text-text-muted">
                {q ? "No cases found" : "No open cases"}
              </p>
            )}
          </div>

          <div className="border-t border-border p-1">
            <button
              type="button"
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-accent-600 hover:bg-neutral-100 dark:hover:bg-neutral-800"
              onClick={() => {
                navigate("/cases");
                setOpen(false);
              }}
            >
              View all cases
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
